export function scoreBandLabel(score: number): string {
  if (score >= 95) return "最高水準の回答です。この調子で本番に臨もう。";
  if (score >= 75) return "面接通過水準です。自信を持って本番に臨もう。";
  if (score >= 60)
    return "合格圏内まであと少しです。改善ポイントを押さえて突破しよう。";
  return "重点的な練習が必要です。一つずつ克服していこう。";
}

export function scoreColor(score: number): string {
  if (score >= 95) return "text-gold";
  if (score >= 75) return "text-success";
  if (score >= 60) return "text-warning";
  return "text-error";
}

export function barColor(score: number): string {
  if (score >= 95) return "bg-gold";
  if (score >= 75) return "bg-success";
  if (score >= 60) return "bg-warning";
  return "bg-error";
}

export function rationaleBgColor(score: number): string {
  if (score >= 95) return "bg-gold/5";
  if (score >= 75) return "bg-success/5";
  if (score >= 60) return "bg-warning/5";
  return "bg-error/5";
}

export function rankColor(rank: string): string {
  if (rank === "S") return "text-gold";
  if (rank === "A") return "text-success";
  if (rank === "B") return "text-warning";
  return "text-error";
}

export function rankBgColor(rank: string): string {
  if (rank === "S") return "bg-gold/15";
  if (rank === "A") return "bg-success/15";
  if (rank === "B") return "bg-warning/15";
  return "bg-error/15";
}

export function deltaColor(delta: number): string {
  if (delta > 0) return "text-success";
  if (delta < 0) return "text-error";
  return "text-outline";
}

export function formatDelta(delta: number): string {
  if (delta > 0) return `+${delta}`;
  if (delta < 0) return `${delta}`;
  return "±0";
}

export type ScoreKey =
  | "structure_logic"
  | "specificity"
  | "motivation"
  | "self_awareness"
  | "expression";

export const SCORE_LABELS: Record<ScoreKey, string> = {
  structure_logic: "構造性・論理性",
  specificity: "具体性・実績",
  motivation: "志望・動機",
  self_awareness: "自己認識・成長",
  expression: "表現・伝達力",
};

// 社会人基礎力（経産省 3能力12要素）。バックエンドの
// FeedbackGeneratorService::COMPETENCY_ELEMENTS と要素キー・日本語ラベルを一致させる。
export type CompetencyKey =
  | "initiative"
  | "influence"
  | "execution"
  | "problem_finding"
  | "planning"
  | "creativity"
  | "transmission"
  | "listening"
  | "flexibility"
  | "situational_awareness"
  | "discipline"
  | "stress_control";

export const COMPETENCY_LABELS: Record<CompetencyKey, string> = {
  initiative: "主体性",
  influence: "働きかけ力",
  execution: "実行力",
  problem_finding: "課題発見力",
  planning: "計画力",
  creativity: "創造力",
  transmission: "発信力",
  listening: "傾聴力",
  flexibility: "柔軟性",
  situational_awareness: "情況把握力",
  discipline: "規律性",
  stress_control: "ストレスコントロール力",
};

export interface CompetencyAbility {
  label: string;
  elements: CompetencyKey[];
}

// 3能力へのグルーピング（表示順もこの順）
export const COMPETENCY_ABILITIES: CompetencyAbility[] = [
  {
    label: "前に踏み出す力",
    elements: ["initiative", "influence", "execution"],
  },
  {
    label: "考え抜く力",
    elements: ["problem_finding", "planning", "creativity"],
  },
  {
    label: "チームで働く力",
    elements: [
      "transmission",
      "listening",
      "flexibility",
      "situational_awareness",
      "discipline",
      "stress_control",
    ],
  },
];

// 要素キー -> 所属する能力ラベル（バッジ表示用）
export const COMPETENCY_ABILITY_BY_ELEMENT = COMPETENCY_ABILITIES.reduce(
  (acc, ability) => {
    ability.elements.forEach((el) => {
      acc[el] = ability.label;
    });
    return acc;
  },
  {} as Record<CompetencyKey, string>,
);

// 能力ラベル -> バッジ配色
export const COMPETENCY_ABILITY_BADGE_CLASS: Record<string, string> = {
  前に踏み出す力: "bg-primary text-white",
  考え抜く力: "bg-[#7C3AED] text-white",
  チームで働く力: "bg-success text-white",
};

export interface CompetencyItem {
  element: CompetencyKey;
  reason: string;
}

export interface CompetencyFeedback {
  strong: CompetencyItem[];
  weak: CompetencyItem[];
  /** 根拠のある強みが無いときだけ、今後強みにできそうな要素を最大1件持つ（練習モードのみ、#1967）。 */
  potential?: CompetencyItem[];
}

export function categoryBadgeClass(category: string): string {
  switch (category) {
    case "自己紹介":
      return "bg-[#FDF0EE] text-primary";
    case "転職理由":
      return "bg-[#FFF7ED] text-[#EA580C]";
    case "自己分析":
      return "bg-[#F5F3FF] text-[#7C3AED]";
    case "キャリア":
      return "bg-[#EBF5FF] text-[#2563EB]";
    case "実績":
      return "bg-[#ECFDF5] text-[#059669]";
    case "技術課題":
      return "bg-[#EFF6FF] text-[#3B82F6]";
    case "チーム開発":
      return "bg-[#F0FDF4] text-[#16A34A]";
    default:
      return "bg-surface-container-low text-outline";
  }
}
